<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TablePaginator from "@/components/shared/TablePaginator.vue"
import store from "@/plugins/store"
import settlementService, { type ListRequest } from "@/services/settlement"
import type { Partner } from "@/types/partner.type"
import type { Settlement, SettlementScheduled } from "@/types/settlement.type"
import { formattedMoney } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import { formattedDate } from "@/utils/string"

const route = useRoute()
const router = useRouter()

const partner = computed<Partner | null>(() => store.getters.partner)

const payouts = ref<Settlement[]>([])
const total = ref(0)

const query = ref<ListRequest>({
  page: (route.query.page as string) || "1",
  perPage: (route.query.perPage as string) || "25",
  completedAtGteq: (route.query.completedAtGteq as string) || "",
  completedAtLteq: (route.query.completedAtLteq as string) || "",
})

// fetch function
async function loadPayouts() {
  try {
    const params = Object.entries(query.value).reduce((acc, [key, value]) => {
      acc[key as keyof ListRequest] = value === "" ? null : value
      return acc
    }, {} as ListRequest)

    // TODO : timezone
    if (params.completedAtGteq) {
      const date = new Date(`${params.completedAtGteq}T00:00:00`)
      params.completedAtGteq = date.toISOString()
    }
    if (params.completedAtLteq) {
      const date = new Date(`${params.completedAtLteq}T23:59:59`)
      params.completedAtLteq = date.toISOString()
    }

    const data = await settlementService.list(params)
    payouts.value = data.items
    total.value = data.total
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

const scheduledAmounts = ref<SettlementScheduled | null>(null)

async function loadScheduledAmounts() {
  try {
    const data = await settlementService.scheduled()
    scheduledAmounts.value = data
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadPayouts()
  await loadScheduledAmounts()
})

// re-load whenever the query string changes
watch(
  () => route.query,
  (newQuery) => {
    query.value = {
      page: (newQuery.page as string) || "1",
      perPage: (newQuery.perPage as string) || "25",
      completedAtGteq: (newQuery.completedAtGteq as string) || "",
      completedAtLteq: (newQuery.completedAtLteq as string) || "",
    }
    loadPayouts()
  },
)

// update URL (and trigger the watch) when the form submits
function searchPayouts() {
  query.value.page = "1"
  const params = Object.fromEntries(Object.entries(query.value).filter(([_, v]) => v !== ""))

  router.push({ path: route.path, query: params })
}
</script>

<template>
  <LayoutAuthed :title="$t('nav.payouts')">
    <Card>
      <CardTitle>{{ $t("shared.search") }}</CardTitle>
      <form @submit.prevent="searchPayouts()">
        <CardContent>
          <div class="row">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox
                v-model="query.completedAtGteq"
                :label="$t('payout.completedDateFrom')"
                type="date"
                field="completedDateFrom"
              />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox
                v-model="query.completedAtLteq"
                :label="$t('payout.completedDateTo')"
                type="date"
                field="completedDateTo"
              />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button type="submit" :text="$t('shared.search')" />
        </CardActions>
      </form>
    </Card>
    <Card v-if="scheduledAmounts" class="scheduled-amounts">
      <CardContent>
        <CardSubtitle>{{ $t("payout.scheduledPayout") }}</CardSubtitle>
        <div class="row">
          <div class="col-12 col-lg-6 col-xxl-6">
            <InfoTable>
              <InfoRow :name="$t('payout.supplyAmount')">
                {{ formattedMoney(scheduledAmounts.supplyAmount, partner?.tradingCurrency || null) }}
              </InfoRow>
              <InfoRow :name="$t('payout.tax')">
                {{ formattedMoney(scheduledAmounts.tax, partner?.tradingCurrency || null) }}
              </InfoRow>
              <InfoRow :name="$t('payout.amount')">
                {{ formattedMoney(scheduledAmounts.amount, partner?.tradingCurrency || null) }}
              </InfoRow>
            </InfoTable>
          </div>
          <div class="col-12 col-lg-6 col-xxl-6">
            <InfoTable>
              <InfoRow :name="$t('payout.scheduledAt')">{{ $t("payout.guides.scheduledEveryHour") }}</InfoRow>
            </InfoTable>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardTitle>{{ $t("shared.results") }} ({{ total }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("payout.id") }}</th>
              <th>{{ $t("payout.supplyAmount") }}</th>
              <th>{{ $t("payout.tax") }}</th>
              <th>{{ $t("payout.amount") }}</th>
              <th>{{ $t("payout.completedDate") }}</th>
              <th>{{ $t("payout.status") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(payout, index) in payouts" :key="index">
              <td>
                <router-link :to="`/payouts/${payout.id}`">{{ payout.id }}</router-link>
              </td>
              <td>{{ formattedMoney(payout.supplyAmount, payout.currency) }}</td>
              <td>{{ formattedMoney(payout.tax, payout.currency) }}</td>
              <td>{{ formattedMoney(payout.amount, payout.currency) }}</td>
              <td>{{ formattedDate(payout.completedAt) }}</td>
              <td>
                <StatusBadge model="payout" field="status" :value="payout.status" />
              </td>
            </tr>
          </template>
        </Table>
        <TablePaginator
          :total="total"
          :perPage="Number(query.perPage)"
          :page="Number(query.page)"
          :url="route.fullPath"
        />
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped>
.scheduled-amounts {
  background-color: var(--bs-danger-bg-subtle);
}

.scheduled-amounts ::v-deep(th),
.scheduled-amounts ::v-deep(td) {
  background-color: var(--bs-danger-bg-subtle);
}
</style>
